import { useEffect, useState } from 'react';
import { apiRequest } from '../utils/auth';
import { newestFirst } from '../utils/dateSort';

interface WaitlistUser {
  _id: string;
  userId: string;
  email?: string;
  name?: string;
  preferredName?: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  approvedAt?: string;
  approvedBy?: string;
}

export default function WaitlistPage() {
  const [waitlistUsers, setWaitlistUsers] = useState<WaitlistUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchWaitlist = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/api/admin/creator-waitlist');
      setWaitlistUsers(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch waitlist');
      console.error('Error fetching waitlist:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWaitlist();
  }, []);

  const handleApprove = async (userId: string) => {
    if (!window.confirm('Are you sure you want to approve this user for public lens permission?')) {
      return;
    }

    try {
      setProcessingUserId(userId);
      await apiRequest(`/api/admin/creator-waitlist/${userId}/approve`, {
        method: 'POST',
      });
      
      // Refresh the list
      await fetchWaitlist();
      alert('User approved for public lens permission!');
    } catch (err: any) {
      alert(err.message || 'Failed to approve user for public lens permission');
      console.error('Error approving user:', err);
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleReject = async (userId: string) => {
    if (!window.confirm('Are you sure you want to reject this user\'s public lens permission request?')) {
      return;
    }

    try {
      setProcessingUserId(userId);
      await apiRequest(`/api/admin/creator-waitlist/${userId}/reject`, {
        method: 'POST',
      });
      
      // Refresh the list
      await fetchWaitlist();
      alert('User\'s public lens permission request rejected.');
    } catch (err: any) {
      alert(err.message || 'Failed to reject public lens permission request');
      console.error('Error rejecting user:', err);
    } finally {
      setProcessingUserId(null);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <h1>Public Lens Waitlist</h1>
        <p style={{ color: 'var(--color-muted)' }}>Newest requests first</p>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <h1>Public Lens Waitlist</h1>
        <p style={{ color: 'var(--color-muted)' }}>Newest requests first</p>
        <p style={{ color: 'var(--color-danger)' }}>Error: {error}</p>
        <button onClick={fetchWaitlist}>Retry</button>
      </div>
    );
  }

  const pendingUsers = newestFirst(waitlistUsers.filter(u => u.status === 'pending'), user => user.requestedAt);
  const processedUsers = newestFirst(waitlistUsers.filter(u => u.status !== 'pending'), user => user.requestedAt);

  return (
    <div className="page">
      <h1>Public Lens Waitlist</h1>
      <p style={{ color: 'var(--color-muted)' }}>Newest requests first</p>
      
      <div style={{ marginBottom: '30px' }}>
        <h2>Pending Requests ({pendingUsers.length})</h2>
        {pendingUsers.length === 0 ? (
          <p>No pending requests</p>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {pendingUsers.map((user) => (
              <div key={user._id} style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--color-text)', fontSize: '15px' }}>{user.preferredName || user.name || '-'}</div>
                  {user.email && <div style={{ fontSize: '13px', color: 'var(--color-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--color-muted)' }}>
                  <span style={{ color: 'var(--color-subtle)' }}>Requested: </span>{new Date(user.requestedAt).toLocaleString()}
                </div>
                <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--color-border)', paddingTop: '10px' }}>
                  <button
                    onClick={() => handleApprove(user.userId)}
                    disabled={processingUserId === user.userId}
                    style={{ flex: 1, padding: '8px 12px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: '500', cursor: processingUserId === user.userId ? 'not-allowed' : 'pointer', opacity: processingUserId === user.userId ? 0.6 : 1 }}
                  >
                    {processingUserId === user.userId ? 'Processing...' : 'Approve'}
                  </button>
                  <button
                    onClick={() => handleReject(user.userId)}
                    disabled={processingUserId === user.userId}
                    style={{ flex: 1, padding: '8px 12px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: '500', cursor: processingUserId === user.userId ? 'not-allowed' : 'pointer', opacity: processingUserId === user.userId ? 0.6 : 1 }}
                  >
                    {processingUserId === user.userId ? 'Processing...' : 'Reject'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-surface-hover)', borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Email</th>
                <th aria-sort="descending" style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Requested At ↓</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingUsers.map((user) => (
                <tr key={user._id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '12px', color: 'var(--color-text)' }}>{user.preferredName || user.name || '-'}</td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)' }}>{user.email || '-'}</td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)' }}>
                    {new Date(user.requestedAt).toLocaleString()}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <button
                      onClick={() => handleApprove(user.userId)}
                      disabled={processingUserId === user.userId}
                      style={{
                        padding: '6px 12px',
                        marginRight: '8px',
                        backgroundColor: 'var(--color-success-strong)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: processingUserId === user.userId ? 'not-allowed' : 'pointer',
                        opacity: processingUserId === user.userId ? 0.6 : 1,
                      }}
                    >
                      {processingUserId === user.userId ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleReject(user.userId)}
                      disabled={processingUserId === user.userId}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'var(--color-danger-strong)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: processingUserId === user.userId ? 'not-allowed' : 'pointer',
                        opacity: processingUserId === user.userId ? 0.6 : 1,
                      }}
                    >
                      {processingUserId === user.userId ? 'Processing...' : 'Reject'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2>Processed Entries ({processedUsers.length})</h2>
        {processedUsers.length === 0 ? (
          <p>No processed entries</p>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {processedUsers.map((user) => (
              <div key={user._id} style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontWeight: 600, color: 'var(--color-text)', fontSize: '15px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.preferredName || user.name || '-'}</span>
                  <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: user.status === 'approved' ? 'var(--color-success-strong)' : 'var(--color-danger-strong)', color: 'white', fontSize: '12px', flexShrink: 0 }}>
                    {user.status}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', borderTop: '1px solid var(--color-border)', paddingTop: '10px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--color-subtle)', marginBottom: '2px' }}>Requested At</div>
                    <div style={{ fontSize: '13px', color: 'var(--color-muted)' }}>{new Date(user.requestedAt).toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--color-subtle)', marginBottom: '2px' }}>Processed At</div>
                    <div style={{ fontSize: '13px', color: 'var(--color-muted)' }}>{user.approvedAt ? new Date(user.approvedAt).toLocaleString() : '-'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-surface-hover)', borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Status</th>
                <th aria-sort="descending" style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Requested At ↓</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Processed At</th>
              </tr>
            </thead>
            <tbody>
              {processedUsers.map((user) => (
                <tr key={user._id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '12px', color: 'var(--color-text)' }}>{user.preferredName || user.name || '-'}</td>
                  <td style={{ padding: '12px' }}>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: user.status === 'approved' ? 'var(--color-success-strong)' : 'var(--color-danger-strong)',
                        color: 'white',
                        fontSize: '12px',
                      }}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)' }}>
                    {new Date(user.requestedAt).toLocaleString()}
                  </td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)' }}>
                    {user.approvedAt ? new Date(user.approvedAt).toLocaleString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
